import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Send, Upload, X, FileImage, FileVideo, MessageSquare, User, Shield, CheckCircle, XCircle, ArrowLeft, Home, ZoomIn } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Format date to Mexico timezone
const formatDateMexico = (dateStr) => {
  const date = new Date(dateStr);
  return date.toLocaleString("es-MX", {
    timeZone: "America/Mexico_City",
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true
  });
};

// Handle paste for images
const handlePasteImages = (e, setFiles) => {
  const items = e.clipboardData?.items;
  if (!items) return;
  
  const imageFiles = [];
  for (let i = 0; i < items.length; i++) {
    if (items[i].type.startsWith('image/')) {
      const file = items[i].getAsFile();
      if (file) {
        if (file.size > 15 * 1024 * 1024) {
          toast.error("La imagen pegada excede 15MB");
          continue;
        }
        const newFile = new File([file], `captura_${Date.now()}.png`, { type: file.type });
        imageFiles.push(newFile);
      }
    }
  }
  
  if (imageFiles.length > 0) {
    setFiles(prev => [...prev, ...imageFiles]);
    toast.success(`${imageFiles.length} imagen(es) pegada(s)`);
  }
};

export default function FeedbackConversation() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const [closing, setClosing] = useState(false);
  const messagesEndRef = useRef(null);

  // Image viewer state
  const [imageViewerOpen, setImageViewerOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  const openImageViewer = (imageData, contentType, filename) => {
    setSelectedImage({ data: imageData, contentType, filename });
    setImageViewerOpen(true);
  };

  const fetchData = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/feedback/token/${token}`);
      setData(response.data);
    } catch (error) {
      toast.error("No se pudo cargar la conversación");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    // Scroll to bottom when messages change
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [data?.messages]);

  const handleFileChange = (e) => {
    const newFiles = Array.from(e.target.files);
    const validFiles = newFiles.filter(file => {
      if (file.size > 15 * 1024 * 1024) {
        toast.error(`${file.name} excede 15MB`);
        return false;
      }
      return true;
    });
    setFiles(prev => [...prev, ...validFiles]);
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSendMessage = async () => {
    if (!message.trim() && files.length === 0) {
      toast.error("Escribe un mensaje o adjunta un archivo");
      return;
    }

    setSending(true);
    const formData = new FormData();
    formData.append("message", message);
    files.forEach(file => {
      formData.append("files", file);
    });

    try {
      await axios.post(`${API}/feedback/token/${token}/reply`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success("Mensaje enviado");
      setMessage("");
      setFiles([]);
      fetchData();
    } catch (error) {
      toast.error("Error al enviar mensaje");
    } finally {
      setSending(false);
    }
  };

  const handleCloseCase = async () => {
    setClosing(true);
    try {
      await axios.post(`${API}/feedback/token/${token}/close`);
      toast.success("Caso cerrado correctamente");
      fetchData();
    } catch (error) {
      toast.error("Error al cerrar el caso");
    } finally {
      setClosing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-muted-foreground">Cargando conversación...</div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <XCircle className="w-16 h-16 text-destructive mx-auto mb-4" />
          <h2 className="text-xl font-bold mb-2">Conversación no encontrada</h2>
          <p className="text-muted-foreground">El enlace puede haber expirado o ser inválido</p>
        </div>
      </div>
    );
  }

  const { correction, category_name, messages } = data;
  const isClosed = correction.status !== "retroalimentacion";

  return (
    <div className="min-h-screen bg-background p-4 md:p-8" data-testid="feedback-conversation">
      {/* Header */}
      <header className="max-w-4xl mx-auto mb-6">
        {/* Back button - más visible */}
        <Button
          onClick={() => navigate("/")}
          className="mb-6 bg-primary/20 hover:bg-primary/30 text-primary border border-primary/50 px-6 py-3 text-base font-semibold uppercase tracking-wider"
          data-testid="back-to-home-button"
        >
          <ArrowLeft className="w-5 h-5 mr-2" /> Volver al Panel Principal
        </Button>

        <div className="flex items-center gap-3 mb-4">
          <div className="p-3 rounded-md bg-primary/20">
            <MessageSquare className="w-6 h-6 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold uppercase tracking-wider">
              Retroalimentación
            </h1>
            <p className="text-sm text-muted-foreground">
              {category_name} - {correction.title}
            </p>
          </div>
        </div>

        {/* Status and Close Button */}
        <div className="flex items-center justify-between">
          <Badge className={`text-xs uppercase ${
            correction.status === "retroalimentacion" 
              ? "bg-purple-900/30 text-purple-400 border border-purple-900/50"
              : correction.status === "completado"
              ? "status-completado"
              : "status-pendiente"
          }`}>
            {correction.status === "retroalimentacion" ? "Retroalimentación" : correction.status}
          </Badge>
          
          {!isClosed && (
            <Button
              variant="outline"
              onClick={handleCloseCase}
              disabled={closing}
              className="border-green-900/50 text-green-400 hover:bg-green-900/20"
              data-testid="close-case-button"
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              {closing ? "Cerrando..." : "Cerrar Caso"}
            </Button>
          )}
        </div>
      </header>

      {/* Original Correction Info */}
      <div className="max-w-4xl mx-auto mb-6">
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">
              Corrección Original
            </p>
            <p className="text-sm text-muted-foreground">{correction.description}</p>
            {correction.attachments && correction.attachments.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2">
                {correction.attachments.map((att, i) => (
                  <div 
                    key={i}
                    className="relative group cursor-pointer"
                    onClick={() => openImageViewer(att.data, att.content_type, att.filename)}
                  >
                    <img 
                      src={`data:${att.content_type};base64,${att.data}`}
                      alt={att.filename}
                      className="w-20 h-20 object-cover rounded-sm border border-border"
                    />
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center rounded-sm">
                      <ZoomIn className="w-6 h-6 text-white" />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Messages */}
      <div className="max-w-4xl mx-auto mb-6">
        <div className="space-y-4 max-h-[50vh] overflow-y-auto p-2">
          {messages.map((msg, index) => (
            <div 
              key={msg.id || index}
              className={`flex ${msg.sender === "admin" ? "justify-start" : "justify-end"}`}
            >
              <div className={`max-w-[80%] ${
                msg.sender === "admin" 
                  ? "bg-secondary/50 border-l-2 border-l-primary" 
                  : "bg-primary/20 border-r-2 border-r-green-500"
              } rounded-md p-4`}>
                <div className="flex items-center gap-2 mb-2">
                  {msg.sender === "admin" ? (
                    <Shield className="w-4 h-4 text-primary" />
                  ) : (
                    <User className="w-4 h-4 text-green-400" />
                  )}
                  <span className="text-xs font-semibold uppercase">
                    {msg.sender === "admin" ? "Equipo" : "Tú"}
                  </span>
                  <span className="text-xs text-muted-foreground mono">
                    {formatDateMexico(msg.created_at)}
                  </span>
                </div>
                <p className="text-sm whitespace-pre-wrap">{msg.message}</p>
                
                {msg.attachments && msg.attachments.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {msg.attachments.map((att, i) => (
                      <div key={i} className="relative">
                        {att.content_type?.startsWith("video") ? (
                          <video 
                            src={`data:${att.content_type};base64,${att.data}`}
                            className="w-24 h-24 object-cover rounded-sm border border-border"
                            controls
                          />
                        ) : (
                          <div 
                            className="relative group cursor-pointer"
                            onClick={() => openImageViewer(att.data, att.content_type, att.filename)}
                          >
                            <img 
                              src={`data:${att.content_type};base64,${att.data}`}
                              alt={att.filename}
                              className="w-24 h-24 object-cover rounded-sm border border-border"
                            />
                            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center rounded-sm">
                              <ZoomIn className="w-6 h-6 text-white" />
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Reply Form */}
      {!isClosed ? (
        <div className="max-w-4xl mx-auto">
          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <Textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onPaste={(e) => handlePasteImages(e, setFiles)}
                placeholder="Escribe tu respuesta... (Ctrl+V para pegar imágenes)"
                className="bg-input border-border min-h-[100px] mb-4"
                data-testid="feedback-message-input"
              />
              
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => document.getElementById("feedback-file-input").click()}
                    className="border-border"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Adjuntar
                  </Button>
                  <span className="text-xs text-muted-foreground hidden sm:inline">o Ctrl+V</span>
                  <input
                    id="feedback-file-input"
                    type="file"
                    multiple
                    accept="image/*,video/*"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                  
                  {files.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {files.map((file, index) => (
                        <div key={index} className="flex items-center gap-1 bg-muted px-2 py-1 rounded-sm text-xs">
                          {file.type.startsWith("video") ? (
                            <FileVideo className="w-3 h-3" />
                          ) : (
                            <FileImage className="w-3 h-3" />
                          )}
                          <span className="max-w-[80px] truncate">{file.name}</span>
                          <button type="button" onClick={() => removeFile(index)}>
                            <X className="w-3 h-3 text-muted-foreground hover:text-destructive" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                
                <Button
                  onClick={handleSendMessage}
                  disabled={sending}
                  className="bg-primary hover:bg-primary/90"
                  data-testid="send-feedback-button"
                >
                  <Send className="w-4 h-4 mr-2" />
                  {sending ? "Enviando..." : "Enviar"}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      ) : (
        <div className="max-w-4xl mx-auto text-center py-8">
          <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-2">Caso Cerrado</h3>
          <p className="text-muted-foreground text-sm">
            Esta conversación ha sido cerrada. El equipo revisará tu caso.
          </p>
        </div>
      )}

      {/* Image Viewer Dialog */}
      <Dialog open={imageViewerOpen} onOpenChange={setImageViewerOpen}>
        <DialogContent className="bg-card border-border max-w-4xl max-h-[90vh] p-2">
          <DialogHeader className="p-2">
            <DialogTitle className="text-sm text-muted-foreground">
              {selectedImage?.filename || "Imagen"}
            </DialogTitle>
          </DialogHeader>
          {selectedImage && (
            <div className="flex items-center justify-center overflow-auto max-h-[80vh]">
              <img 
                src={`data:${selectedImage.contentType};base64,${selectedImage.data}`}
                alt={selectedImage.filename}
                className="max-w-full max-h-[75vh] object-contain rounded-sm"
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
